import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import Landing from './pages/Landing';
import CompanyDashboard from './pages/CompanyDashboard';
import CandidateDashboard from './pages/CandidateDashboard';
import AdminDashboard from './pages/AdminDashboard';
import VideoRoom from './pages/VideoRoom';
import CommunityHub from './pages/CommunityHub';
import Marketplace from './pages/Marketplace';
import Mentorship from './pages/Mentorship';
import BackgroundCheck from './pages/BackgroundCheck';
import ResumeBuilder from './pages/ResumeBuilder';
import InterviewPrep from './pages/InterviewPrep';
import Profile from './pages/Profile';
import CandidateBrowser from './pages/CandidateBrowser';
import Phase1AIInterview from './pages/Phase1AIInterview';
import BecomeMentor from './pages/BecomeMentor';
import PrivacyPolicy from './pages/PrivacyPolicy';
import BackgroundCheckPolicy from './pages/BackgroundCheckPolicy';
import TermsOfService from './pages/TermsOfService';
import IdleTimeout from './components/IdleTimeout';
import AIAssistantDock from './components/AIAssistantDock';
import useDeviceLayout from './hooks/useDeviceLayout';
import { initializeMetaPixel } from './lib/metaPixel';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let sessionRedirectInProgress = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || '';
    const isCredentialAttempt = requestUrl.includes('/auth/login')
      || requestUrl.includes('/auth/register')
      || requestUrl.includes('/auth/account');
    if (error.response?.status === 401 && localStorage.getItem('token') && !isCredentialAttempt) {
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      if (!sessionRedirectInProgress) {
        sessionRedirectInProgress = true;
        window.location.replace('/');
      }
    }
    return Promise.reject(error);
  }
);

function PrivateRoute({ children, role }) {
  const token = localStorage.getItem('token');
  const userRole = localStorage.getItem('role');
  
  if (!token) {
    return <Navigate to="/" replace />;
  }
  
  if (role && userRole !== role && userRole !== 'admin') {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
  const device = useDeviceLayout();

  useEffect(() => {
    initializeMetaPixel();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsLoggedIn(true);
      api.get('/auth/me')
        .then(res => setUser(res.data))
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('role');
          setIsLoggedIn(false);
        });
    } else {
      setIsLoggedIn(false);
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return undefined;

    const validateSession = () => {
      if (localStorage.getItem('token')) {
        api.get('/auth/me').catch(() => {});
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') validateSession();
    };
    const interval = window.setInterval(validateSession, 30000);
    window.addEventListener('focus', validateSession);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', validateSession);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isLoggedIn]);

  // Listen for storage changes (logout from IdleTimeout)
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem('token');
      setIsLoggedIn(!!token);
      if (!token) {
        setUser(null);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <div className={`App device-${device.type} orientation-${device.orientation}`} data-device={device.type}>
      <BrowserRouter>
        <IdleTimeout isLoggedIn={isLoggedIn} />
        <Routes>
          <Route path="/" element={<Landing setUser={setUser} />} />
          <Route path="/register" element={<Landing setUser={setUser} initialAuthMode="register" />} />
          <Route path="/employers/register" element={<Landing setUser={setUser} initialAuthMode="register" registrationAudience="employer" />} />
          <Route path="/employer/register" element={<Navigate to="/employers/register" replace />} />
          <Route path="/register/company" element={<Navigate to="/employers/register" replace />} />
          <Route 
            path="/company/dashboard" 
            element={
              <PrivateRoute role="company">
                <CompanyDashboard user={user} />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/candidate/dashboard" 
            element={
              <PrivateRoute role="candidate">
                <CandidateDashboard user={user} />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/admin/dashboard" 
            element={
              <PrivateRoute role="admin">
                <AdminDashboard user={user} />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/video/:bookingId" 
            element={
              <PrivateRoute>
                <VideoRoom user={user} />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/community" 
            element={
              <PrivateRoute>
                <CommunityHub user={user} />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/marketplace" 
            element={
              <PrivateRoute>
                <Marketplace user={user} />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/mentorship" 
            element={
              <PrivateRoute>
                <Mentorship user={user} />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/background-check" 
            element={
              <PrivateRoute role="candidate">
                <BackgroundCheck user={user} />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/resume-builder" 
            element={
              <PrivateRoute>
                <ResumeBuilder user={user} />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/interview-prep" 
            element={
              <PrivateRoute>
                <InterviewPrep user={user} />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/profile" 
            element={
              <PrivateRoute>
                <Profile user={user} />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/browse-candidates" 
            element={
              <PrivateRoute role="company">
                <CandidateBrowser user={user} />
              </PrivateRoute>
            } 
          />
          <Route
            path="/ai-interview/:jobId"
            element={
              <PrivateRoute role="candidate">
                <Phase1AIInterview user={user} />
              </PrivateRoute>
            }
          />
          <Route 
            path="/become-mentor" 
            element={
              <PrivateRoute>
                <BecomeMentor user={user} />
              </PrivateRoute>
            } 
          />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/background-check-policy" element={<BackgroundCheckPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
        </Routes>
        <AIAssistantDock />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
